import { useState, useRef, useCallback } from "react";
import Image from "@Components/Image";
import HeadPhoneIcon from "@Icons/HeadPhoneIcon";
import WeChatIcon from "@Icons/WeChatIcon";
import getImagePath from "@Utils/getImagePath";
import wrapperHOC from "@Utils/wrapperHOC";
import classes from "./whitelabelPartnerWeChatQr.module.scss";
import useCustomWindowOpen from "@Components/CustomWindowOpen";

const WhitelabelPartnerWeChatQr = ({ showSupportText = true }: { showSupportText?: boolean }) => {
  const customWindowOpen = useCustomWindowOpen();
  const [isLongPressing, setIsLongPressing] = useState(false);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartTimeRef = useRef<number>(0);

  const handleTouchStart = useCallback(() => {
    touchStartTimeRef.current = Date.now();
    setIsLongPressing(false);

    // Set a timer for long press detection (500ms)
    longPressTimerRef.current = setTimeout(() => {
      setIsLongPressing(true);
    }, 500);
  }, []);

  const handleTouchEnd = useCallback(() => {
    const pressDuration = Date.now() - touchStartTimeRef.current;

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    // If long press was detected (pressed for at least 500ms), redirect
    if (pressDuration >= 500) {
      // Redirect to WeChat support
      customWindowOpen({
        url: "https://work.weixin.qq.com/u/vc728a6fefaedce46e?v=5.0.0.198637&bb=a4c2b8e16c",
        target: "_blank",
        isExternal: true,
      });
    }

    setIsLongPressing(false);
  }, [customWindowOpen]);

  const handleTouchCancel = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setIsLongPressing(false);
  }, []);

  return (
    <div className={classes.wechatSupportQRContent}>
      {showSupportText && (
        <div className={classes.wechatSupportQRHeader}>
          <HeadPhoneIcon fill="#000" height={18} width={18} />
          <span>Support</span>
        </div>
      )}
      <div
        className={`${classes.qrCodeContainer} ${isLongPressing ? classes.longPressing : ""}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        role="button"
        tabIndex={0}
      >
        <Image
          src={getImagePath(
            "https://s3.ap-southeast-1.amazonaws.com/static-assets.amberstudent.com/assets/whitelabelwechatsupportqr.png",
          )}
          width={288}
          height={272}
        />
        {isLongPressing && (
          <div className={classes.longPressOverlay}>
            <span>Opening WeChat...</span>
          </div>
        )}
      </div>
      <div className={classes.wechatSupportQRText}>
        <span>Long press the QR code to open in</span>
        <div className={classes.wechatBrand}>
          <WeChatIcon />
          <span>WeChat</span>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(WhitelabelPartnerWeChatQr, {
  componentName: "WhitelabelPartnerWeChatQr",
  showForChina: true,
});
